(() => {
  const VERSION = '2026.08.26.11';
  const previous = window.BHMobileHeader;
  if (previous?.version === VERSION) {
    previous.refresh?.();
    return;
  }
  previous?.destroy?.();

  const controller = new AbortController();
  const { signal } = controller;
  const instances = new Map();
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  let mutationObserver = null;

  const all = (root, selector) => [...root.querySelectorAll(selector)];
  const COMPETING_HEADER_SELECTOR = [
    '.shopify-section-group-header-group sticky-header',
    '.shopify-section-group-header-group .header-wrapper',
    '.shopify-section-group-header-group .announcement-bar-section',
    'body > .announcement-bar-section',
    'body > [id*="announcement" i]',
    'body > [class*="announcement-bar" i]'
  ].join(',');

  function suppressCompetingHeaders() {
    const roots = all(document, '[data-bh-mobile-header]');
    roots.forEach((root, index) => {
      const duplicate = index > 0;
      root.toggleAttribute('data-bh-duplicate-header', duplicate);
      root.closest('.bh-mobile-header-section')?.toggleAttribute('data-bh-duplicate-section', duplicate);
    });

    all(document, COMPETING_HEADER_SELECTOR).forEach(element => {
      if (element.closest('[data-bh-mobile-header], .bh-mobile-header-section, cart-notification')) return;
      element.setAttribute('data-bh-competing-header', '');
    });
  }

  function getScrollTop() {
    return Math.max(
      window.scrollY || 0,
      document.scrollingElement?.scrollTop || 0,
      document.documentElement.scrollTop || 0,
      document.body?.scrollTop || 0
    );
  }

  function updateHeaderState() {
    const rootElement = document.documentElement;
    const wasCompact = rootElement.classList.contains('bh-mobile-header-compact');
    const menuIsOpen = [...instances.values()].some(instance => instance.root.classList.contains('is-menu-open'));
    const scrollTop = getScrollTop();

    const hideAt = desktopQuery.matches ? 34 : 2;

    /* En móvil reacciona desde el primer gesto. En escritorio espera únicamente
       a que el anuncio haya salido del viewport, justo cuando Dawn llega arriba. */
    const shouldCompact = !menuIsOpen && (wasCompact ? scrollTop > 0 : scrollTop > hideAt);

    rootElement.classList.toggle('bh-mobile-header-compact', shouldCompact);
    instances.forEach(instance => instance.root.classList.toggle('is-compact', shouldCompact));
  }

  function close(instance, restoreFocus = true) {
    const opened = instance.root.querySelector('.bh-mobile-header__drawer.is-open');
    instance.menu.classList.remove('is-open');
    instance.search.classList.remove('is-open');
    instance.menu.setAttribute('aria-hidden', 'true');
    instance.search.setAttribute('aria-hidden', 'true');
    instance.menuButton.setAttribute('aria-expanded', 'false');
    instance.searchButton.setAttribute('aria-expanded', 'false');
    instance.root.classList.remove('is-menu-open');
    instance.backdrop.classList.remove('is-visible');
    document.documentElement.classList.remove('bh-mobile-overlay-open');
    document.documentElement.classList.remove('bh-mobile-menu-open');
    document.body?.classList.remove('bh-mobile-menu-open');
    updateHeaderState();
    window.setTimeout(() => {
      if (!instance.root.querySelector('.bh-mobile-header__drawer.is-open')) instance.backdrop.hidden = true;
    }, 260);
    if (restoreFocus && opened) {
      const trigger = opened === instance.menu ? instance.menuButton : instance.searchButton;
      trigger.focus({ preventScroll: true });
    }
  }

  function showMenuPanel(instance, panelName) {
    const next = instance.menu.querySelector(`[data-bh-menu-panel="${panelName}"]`);
    if (!next || next.classList.contains('is-active')) return;
    const current = instance.menu.querySelector('[data-bh-menu-panel].is-active');
    next.hidden = false;
    next.classList.remove('is-leaving');
    requestAnimationFrame(() => next.classList.add('is-active'));
    if (current) {
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      window.setTimeout(() => {
        current.classList.remove('is-leaving');
        current.hidden = true;
      }, 290);
    }
    next.scrollTop = 0;
  }

  function resetMenu(instance) {
    instance.menu.querySelectorAll('[data-bh-menu-panel]').forEach(panel => {
      const main = panel.dataset.bhMenuPanel === 'main';
      panel.hidden = !main;
      panel.classList.toggle('is-active', main);
      panel.classList.remove('is-leaving');
    });
  }

  function open(instance, panel) {
    close(instance, false);
    instance.backdrop.hidden = false;
    requestAnimationFrame(() => instance.backdrop.classList.add('is-visible'));
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bh-mobile-overlay-open');
    if (panel === instance.menu) {
      resetMenu(instance);
      instance.root.classList.add('is-menu-open');
      document.documentElement.classList.add('bh-mobile-menu-open');
      document.body?.classList.add('bh-mobile-menu-open');
      updateHeaderState();
    }
    const button = panel === instance.menu ? instance.menuButton : instance.searchButton;
    button.setAttribute('aria-expanded', 'true');
    window.setTimeout(() => {
      const focusTarget = panel.querySelector('[data-bh-search-input], button, a, summary');
      focusTarget?.focus({ preventScroll: true });
    }, 180);
  }

  async function syncCartCount() {
    const instance = instances.values().next().value;
    if (!instance) return;
    try {
      const response = await fetch(instance.root.dataset.cartJsonUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-bh-cart-count]').forEach(counter => {
        counter.textContent = cart.item_count;
        counter.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (error) {
      console.warn('[BH Mobile Header] No se pudo sincronizar el carrito.', error);
    }
  }

  function mount(root) {
    if (instances.has(root)) return;
    const instance = {
      root,
      menu: root.querySelector('[data-bh-menu]'),
      search: root.querySelector('[data-bh-search]'),
      backdrop: root.querySelector('[data-bh-backdrop]'),
      menuButton: root.querySelector('[data-bh-menu-open]'),
      searchButton: root.querySelector('[data-bh-search-open]'),
      menuNavigation: root.querySelector('[data-bh-menu-navigation]')
    };
    if (Object.values(instance).some(value => !value)) return;
    root.dataset.bhMounted = 'true';
    instances.set(root, instance);

    instance.menuButton.addEventListener('click', () => {
      if (instance.menu.classList.contains('is-open')) close(instance);
      else open(instance, instance.menu);
    }, { signal });
    instance.searchButton.addEventListener('click', () => open(instance, instance.search), { signal });
    instance.backdrop.addEventListener('click', () => close(instance), { signal });
    all(root, '[data-bh-close]').forEach(button => button.addEventListener('click', () => close(instance), { signal }));
    all(root, '[data-bh-menu-forward]').forEach(button => {
      button.addEventListener('click', () => showMenuPanel(instance, button.dataset.bhMenuForward), { signal });
    });
    all(root, '[data-bh-menu-back]').forEach(button => {
      button.addEventListener('click', () => showMenuPanel(instance, 'main'), { signal });
    });
  }

  function refresh() {
    document.documentElement.classList.add('bh-has-mobile-header');
    suppressCompetingHeaders();
    instances.forEach((instance, root) => {
      if (!root.isConnected) instances.delete(root);
    });
    all(document, '[data-bh-mobile-header]:not([data-bh-duplicate-header])').forEach(mount);

    updateHeaderState();
    syncCartCount();
  }

  window.addEventListener('scroll', updateHeaderState, { passive: true, signal });
  window.addEventListener('resize', updateHeaderState, { passive: true, signal });
  window.addEventListener('pageshow', updateHeaderState, { signal });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    instances.forEach(instance => close(instance));
  }, { signal });

  ['cart:refresh', 'cart:updated', 'cart:change', 'product:added', 'ajaxProduct:added'].forEach(name => {
    document.addEventListener(name, syncCartCount, { signal });
  });
  if (window.subscribe && window.PUB_SUB_EVENTS?.cartUpdate) {
    try { window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, syncCartCount); } catch (_) {}
  }

  mutationObserver = new MutationObserver(records => {
    const hasRelevantAddition = records.some(record => [...record.addedNodes].some(node => node instanceof Element));
    if (hasRelevantAddition) suppressCompetingHeaders();
    const hasNewOwnHeader = records.some(record => [...record.addedNodes].some(node =>
      node instanceof Element && (node.matches('[data-bh-mobile-header]') || node.querySelector?.('[data-bh-mobile-header]'))
    ));
    if (hasNewOwnHeader) refresh();
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  window.BHMobileHeader = {
    version: VERSION,
    refresh,
    syncCartCount,
    destroy() {
      controller.abort();
      mutationObserver?.disconnect();
      document.querySelectorAll('[data-bh-mobile-header]').forEach(root => delete root.dataset.bhMounted);
      document.documentElement.classList.remove('bh-mobile-header-compact');
      instances.clear();
    },
    getState: () => ({
      instances: instances.size,
      compact: document.documentElement.classList.contains('bh-mobile-header-compact'),
      scrollTop: getScrollTop()
    })
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true, signal });
  else refresh();
})();
